import { apiClient } from './apiClient';

export type SalesProductCapabilityCategory =
  | 'Module'
  | 'AddOn'
  | 'Service'
  | 'Limit';

export type SalesProductCapabilityBillingUnit =
  | 'Flat'
  | 'PerSeat'
  | 'PerUnit'
  | 'Usage';

export type SalesProductCapability = {
  id: string;
  name: string;
  description: string;
  category: SalesProductCapabilityCategory;
  billingUnit: SalesProductCapabilityBillingUnit;
  isActive: boolean;
  sortOrder: number;
  createdAtUtc: string;
  updatedAtUtc: string;
};

export type SalesProductCapabilityListResult = {
  items: SalesProductCapability[];
  totalCount: number;
  page: number;
  pageSize: number;
};

export type SalesProductCapabilityUpsertPayload = {
  name: string;
  description?: string;
  category?: SalesProductCapabilityCategory;
  billingUnit?: SalesProductCapabilityBillingUnit;
  isActive?: boolean;
  sortOrder?: number;
};

export type SalesProductCapabilityLink = {
  capabilityId: string;
  name: string;
  category: SalesProductCapabilityCategory;
  billingUnit: SalesProductCapabilityBillingUnit;
  isIncluded: boolean;
  quantityDefault?: number | null;
  sortOrder: number;
};

export type SalesProductCapabilityLinkItemPayload = {
  capabilityId: string;
  isIncluded?: boolean;
  quantityDefault?: number | null;
  sortOrder: number;
};

export type SalesProductCapabilityListFilter = {
  activeOnly?: boolean;
  page?: number;
  pageSize?: number;
  search?: string;
};

export async function listSalesCapabilities(
  filter: SalesProductCapabilityListFilter = {},
): Promise<SalesProductCapabilityListResult> {
  const response = await apiClient.get<SalesProductCapabilityListResult>('/v1/sales/capabilities', {
    params: {
      activeOnly: filter.activeOnly ?? false,
      page: filter.page ?? 1,
      pageSize: filter.pageSize ?? 25,
      search: filter.search || undefined,
    },
  });
  return response.data;
}

export async function getSalesCapability(id: string): Promise<SalesProductCapability> {
  const response = await apiClient.get<SalesProductCapability>(`/v1/sales/capabilities/${id}`);
  return response.data;
}

export async function createSalesCapability(
  payload: SalesProductCapabilityUpsertPayload,
): Promise<SalesProductCapability> {
  const response = await apiClient.post<SalesProductCapability>('/v1/sales/capabilities', payload);
  return response.data;
}

export async function updateSalesCapability(
  id: string,
  payload: SalesProductCapabilityUpsertPayload,
): Promise<SalesProductCapability> {
  const response = await apiClient.put<SalesProductCapability>(`/v1/sales/capabilities/${id}`, payload);
  return response.data;
}

export async function deleteSalesCapability(id: string): Promise<void> {
  await apiClient.delete(`/v1/sales/capabilities/${id}`);
}

export async function getProductCapabilities(productId: string): Promise<SalesProductCapabilityLink[]> {
  const response = await apiClient.get<SalesProductCapabilityLink[]>(
    `/v1/sales/products/${productId}/capabilities`,
  );
  return response.data;
}

export async function replaceProductCapabilities(
  productId: string,
  items: SalesProductCapabilityLinkItemPayload[],
): Promise<void> {
  await apiClient.put(`/v1/sales/products/${productId}/capabilities`, { items });
}
